// InvitaRubrica.tsx — v046: «Invita dalla rubrica», come WhatsApp. Si scelgono le persone dalla rubrica del telefono
// (nelle app: il permesso dei contatti; nel browser: il selettore dei contatti di Chrome su Android) e a ognuna si
// manda il messaggio già scritto con il link, per SMS o WhatsApp, con un tocco. I contatti NON vanno mai al nostro
// server: restano sul telefono. Se la rubrica non c'è (computer, iPhone dal sito), il pulsante non si vede.
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { linkInvitoSms, linkWhatsapp, rubricaDisponibile, scegliContatti } from "../servizi/contatti.ts";
import { vibra } from "../servizi/nativo.ts";

interface Scelto {
  nome: string;
  telefono: string;
}

function apri(url: string): void {
  // sms: si apre nella stessa pagina (l'app dei messaggi prende il controllo); WhatsApp in una scheda/app a parte
  if (url.startsWith("sms:")) window.location.href = url;
  else window.open(url, "_blank", "noopener");
}

export function InvitaRubrica({ link, testo, compatto = false }: { link: string; testo: string; compatto?: boolean }) {
  const { t } = useTranslation();
  const [scelti, setScelti] = useState<Scelto[] | null>(null);
  const [mandati, setMandati] = useState<ReadonlySet<string>>(new Set());
  const [errore, setErrore] = useState(false);
  if (!rubricaDisponibile()) return null;
  const messaggio = `${testo} ${link}`;

  const scegli = async () => {
    vibra("leggero");
    setErrore(false);
    try {
      const c = await scegliContatti();
      const righe = c.flatMap((x) => (x.telefoni[0] ? [{ nome: x.nome || x.telefoni[0], telefono: x.telefoni[0] }] : []));
      // nelle app si sceglie una persona per volta: si aggiunge all'elenco (senza doppioni)
      setScelti((prima) => {
        const tutti = [...(prima ?? []), ...righe];
        return tutti.filter((x, i) => tutti.findIndex((y) => y.telefono === x.telefono) === i);
      });
    } catch {
      setErrore(true);
    }
  };
  const manda = (s: Scelto, come: "sms" | "whatsapp") => {
    vibra("medio");
    setMandati((m) => new Set(m).add(s.telefono));
    apri(come === "sms" ? linkInvitoSms([s.telefono], messaggio) : linkWhatsapp(s.telefono, messaggio));
  };

  return (
    <div className="ld-rubrica" data-compatto={compatto || undefined}>
      <button type="button" className="pulsante ld-rubrica-apri" onClick={() => void scegli()}>
        <span aria-hidden="true">📱</span> {scelti?.length ? t("rubrica.altra") : t("rubrica.invita")}
      </button>
      {errore && (
        <p className="nota-centrata" role="status">
          {t("rubrica.errore")}
        </p>
      )}
      {scelti && scelti.length > 0 && (
        <ul className="ld-rubrica-elenco" aria-label={t("rubrica.scelti")}>
          {scelti.map((s) => (
            <li key={s.telefono} className="ld-rubrica-riga" data-mandato={mandati.has(s.telefono) || undefined}>
              <span className="ld-rubrica-nome">
                {mandati.has(s.telefono) && <span aria-hidden="true">✅ </span>}
                {s.nome}
              </span>
              <button type="button" className="pulsante pulsante-piccolo ld-rubrica-wa" onClick={() => manda(s, "whatsapp")} aria-label={t("rubrica.whatsapp_a", { nome: s.nome })}>
                WhatsApp
              </button>
              <button type="button" className="pulsante pulsante-piccolo" onClick={() => manda(s, "sms")} aria-label={t("rubrica.sms_a", { nome: s.nome })}>
                SMS
              </button>
            </li>
          ))}
        </ul>
      )}
      {scelti && scelti.length === 0 && <p className="nota-centrata">{t("rubrica.nessuno")}</p>}
      {scelti && scelti.length > 0 && <p className="nota-centrata">{t("rubrica.nota")}</p>}
    </div>
  );
}
